if (!customElements.get('cart-subscription-toggle')) {
  const sellingPlanCache = {};

  class CartSubscriptionToggle extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
    }

    connectedCallback() {
      this.init();
    }

    get variantId() {
      return parseInt(this.dataset.variantId);
    }

    get lineKey() {
      return this.dataset.lineKey;
    }

    get quantity() {
      return parseInt(this.dataset.quantity);
    }

    get productHandle() {
      return this.dataset.productHandle;
    }

    get currentPlanId() {
      const id = this.dataset.currentPlanId;
      return id ? parseInt(id) : null;
    }

    async init() {
      const plans = await this.fetchSellingPlans();
      if (!plans || plans.length === 0) return;

      this.sellingPlans = plans;
      this.buildUI();
      this.hidden = false;
    }

    async fetchSellingPlans() {
      const handle = this.productHandle;
      if (sellingPlanCache[handle]) return sellingPlanCache[handle];

      try {
        const res = await fetch(`/products/${handle}.js`);
        const product = await res.json();

        if (
          !product.selling_plan_groups ||
          product.selling_plan_groups.length === 0
        ) {
          sellingPlanCache[handle] = [];
          return [];
        }

        const group = product.selling_plan_groups[0];
        const plans = (group.selling_plans || []).map((p) => ({
          id: p.id,
          name: p.name,
        }));
        sellingPlanCache[handle] = plans;
        return plans;
      } catch (_) {
        return [];
      }
    }

    buildUI() {
      const isSub = !!this.currentPlanId;

      const options = this.sellingPlans
        .map(
          (p) =>
            `<option value="${p.id}"${p.id === this.currentPlanId ? ' selected' : ''}>${p.name}</option>`
        )
        .join('');

      this.innerHTML = `
        <div class="cart-subscription-toggle__header">
          <label class="cart-subscription-toggle__label">
            <input
              type="checkbox"
              class="cart-subscription-toggle__checkbox visually-hidden"
              ${isSub ? 'checked' : ''}
            >
            <span class="cart-subscription-toggle__switch" aria-hidden="true">
              <span class="cart-subscription-toggle__switch-thumb"></span>
            </span>
            <span class="cart-subscription-toggle__text">Subscribe + Save 10%</span>
          </label>
        </div>
        <div class="cart-subscription-toggle__options"${isSub ? '' : ' hidden'}>
          <div class="cart-subscription-toggle__frequency-wrapper">
            <select class="cart-subscription-toggle__frequency" aria-label="Delivery frequency">
              ${options}
            </select>
            <span class="cart-subscription-toggle__frequency-arrow" aria-hidden="true">
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </div>
          <p class="cart-subscription-toggle__note">Discount codes cannot be combined with subscription savings.</p>
        </div>
      `;

      if (isSub) this.classList.add('cart-subscription-toggle--active');

      this.checkbox = this.querySelector('.cart-subscription-toggle__checkbox');
      this.optionsEl = this.querySelector('.cart-subscription-toggle__options');
      this.frequency = this.querySelector('.cart-subscription-toggle__frequency');

      this.checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        this.onToggle();
      });
      this.frequency.addEventListener('change', (e) => {
        e.stopPropagation();
        this.onFrequencyChange();
      });
    }

    async onToggle() {
      if (this.busy) return;

      if (this.checkbox.checked) {
        const sellingPlanId = parseInt(this.frequency.value);
        this.optionsEl.hidden = false;
        await this.swapCartItem(sellingPlanId);
      } else {
        this.optionsEl.hidden = true;
        await this.swapCartItem(null);
      }
    }

    async onFrequencyChange() {
      if (this.busy || !this.checkbox.checked) return;
      await this.swapCartItem(parseInt(this.frequency.value));
    }

    async swapCartItem(sellingPlanId) {
      this.busy = true;
      this.classList.add('cart-subscription-toggle--loading');

      try {
        await fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({ id: this.lineKey, quantity: 0 }),
        });

        const addBody = { id: this.variantId, quantity: this.quantity };
        if (sellingPlanId) addBody.selling_plan = sellingPlanId;

        await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify(addBody),
        });

        await this.refreshCart();
      } catch (err) {
        console.error('[subscription-toggle]', err);
        this.busy = false;
        this.classList.remove('cart-subscription-toggle--loading');
      }
    }

    async refreshCart() {
      const drawerItems = document.querySelector('cart-drawer-items');
      const pageItems = document.querySelector(
        'cart-items:not(cart-drawer-items)'
      );

      if (drawerItems) {
        const res = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newItems = doc.querySelector('cart-drawer-items');
        const newFooter = doc.querySelector('.cart-drawer__footer');

        if (newItems) drawerItems.replaceWith(newItems);

        const oldFooter = document.querySelector('.cart-drawer__footer');
        if (newFooter && oldFooter) oldFooter.replaceWith(newFooter);
      }

      if (pageItems) {
        const itemsSectionId =
          document.getElementById('main-cart-items')?.dataset?.id;
        if (itemsSectionId) {
          const res = await fetch(
            `${routes.cart_url}?section_id=${itemsSectionId}`
          );
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector('.js-contents');
          const target = pageItems.querySelector('.js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        }

        const footerEl = document.getElementById('main-cart-footer');
        if (footerEl) {
          const footerSectionId = footerEl.dataset.id;
          const res = await fetch(
            `${routes.cart_url}?section_id=${footerSectionId}`
          );
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector('.js-contents');
          const target = footerEl.querySelector('.js-contents');
          if (source && target) target.innerHTML = source.innerHTML;
        }
      }

      try {
        const res = await fetch(
          `${routes.cart_url}?section_id=cart-icon-bubble`
        );
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('.shopify-section');
        const target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      } catch (_) {}
    }
  }

  customElements.define('cart-subscription-toggle', CartSubscriptionToggle);
}
